import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { NAV_LINKS } from "@/lib/data";
import { Menu, X, ArrowUpRight } from "lucide-react";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-bone/90 backdrop-blur-md border-b border-border/60"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="tr-container flex items-center justify-between h-[72px]">
        <Link to="/" data-testid="header-logo" className="flex items-baseline gap-2 group">
          <span className="font-serif text-[22px] leading-none tracking-tight text-ink">
            ThinkRight
          </span>
          <span className="text-vermilion text-[22px] leading-none font-serif">.</span>
          <span className="overline text-ink/60 hidden sm:inline ml-1">Tech</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[13px] tracking-wide link-underline transition-colors ${
                  isActive ? "text-ink font-medium" : "text-ink/70 hover:text-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <Link
            to="/contact"
            data-testid="header-cta-contact"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold border border-ink px-5 py-3 hover:bg-ink hover:text-white transition-colors"
          >
            Partner with us
            <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 -mr-2 text-ink"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-nav"
          className="lg:hidden border-t border-border bg-bone"
        >
          <div className="tr-container py-8 flex flex-col gap-6">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-2xl font-serif tracking-tight ${
                    isActive ? "text-ink" : "text-ink/70"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-cta-contact"
              className="btn-primary w-fit mt-4"
            >
              Partner with us
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
